import {
  Suspense,
  lazy,
  useEffect,
  useState,
  useRef,
  useCallback,
  type ReactNode,
  type RefObject,
} from 'react';
import { Link, NavLink, useLocation } from '../../router/nextCompat';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../../i18n/useAppTranslation';
import { Search, Menu, X, AlignJustify, ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';
import Image from 'next/image';

import { I18N_DEFAULT_LANGUAGE, I18N_TRANSLATION_ENABLED } from '../../i18n/config';
import {
  buildLocalizedPath,
  isRtlLanguage,
  isSupportedLanguage,
  stripLanguagePrefix,
} from '../../i18n/urlLanguage';
import {
  getLanguageZoneGroups,
  languageOptions,
  type LanguageOption,
  type LanguageZoneId,
  type LanguageZoneGroup,
} from '../../i18n/languageZones';
import { setStorageItem } from '../../utils/browserStorage';
import UniverseMegaMenu from '../layout/UniverseMegaMenu';
import Navbar, { type MegaMenuVariantKey } from './Navbar';
import { MobileAccordionItem, MobileUniversePanel } from './MobileNavMenus';
import AccessibilityTrigger from '../accessibility/AccessibilityTrigger';

const SearchModal = lazy(() => import('../shop/SearchModal'));

/** Délai en ms avant fermeture automatique du méga-menu après mouseleave. */
const MEGA_CLOSE_DELAY_MS = 180;
const SCROLL_OPAQUE_THRESHOLD = 20;
const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

interface NavItem {
  to: string;
  label: string;
  /** If set, hovering this item opens the Navbar mega-menu with this variant. */
  variant?: MegaMenuVariantKey;
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [storesMegaOpen, setStoresMegaOpen] = useState(false);
  const [storesMegaTopPx, setStoresMegaTopPx] = useState(0);
  const [universeOpen, setUniverseOpen] = useState(false);
  const [universeTopPx, setUniverseTopPx] = useState(0);
  const [activeNavVariant, setActiveNavVariant] = useState<MegaMenuVariantKey | null>(null);
  const [mobileExpandedSection, setMobileExpandedSection] = useState<string | null>(null);

  const { t, i18n } = useTranslation();
  const location = useLocation();
  const normalizedPathname = stripLanguagePrefix(location.pathname);
  const hideSearchTrigger = normalizedPathname === '/collections/silent-run';

  const headerShellRef = useRef<HTMLDivElement | null>(null);
  const storesTriggerRef = useRef<HTMLDivElement | null>(null);
  const storesMegaCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const universeCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Mega-panel position helper (shared by Universe + Nav variants) ─────────
  const updateUniverseTop = useCallback(() => {
    const el = headerShellRef.current;
    if (el) setUniverseTopPx(el.getBoundingClientRect().bottom);
  }, []);

  const updateStoresMegaTop = useCallback(() => {
    const el = storesTriggerRef.current;
    if (el) setStoresMegaTopPx(el.getBoundingClientRect().bottom);
  }, []);

  const openStoresMega = useCallback(() => {
    if (storesMegaCloseTimerRef.current) {
      clearTimeout(storesMegaCloseTimerRef.current);
      storesMegaCloseTimerRef.current = null;
    }
    setUniverseOpen(false);
    updateStoresMegaTop();
    setStoresMegaOpen(true);
  }, [updateStoresMegaTop]);

  const scheduleCloseStoresMega = useCallback(() => {
    if (storesMegaCloseTimerRef.current) clearTimeout(storesMegaCloseTimerRef.current);
    storesMegaCloseTimerRef.current = setTimeout(() => {
      setStoresMegaOpen(false);
      storesMegaCloseTimerRef.current = null;
    }, MEGA_CLOSE_DELAY_MS);
  }, []);

  const keepStoresMegaOpen = useCallback(() => {
    if (storesMegaCloseTimerRef.current) {
      clearTimeout(storesMegaCloseTimerRef.current);
      storesMegaCloseTimerRef.current = null;
    }
    setStoresMegaOpen(true);
  }, []);

  const openUniverse = useCallback(() => {
    if (universeCloseTimerRef.current) {
      clearTimeout(universeCloseTimerRef.current);
      universeCloseTimerRef.current = null;
    }
    setStoresMegaOpen(false);
    updateUniverseTop();
    setUniverseOpen(true);
  }, [updateUniverseTop]);

  const scheduleCloseUniverse = useCallback(() => {
    if (universeCloseTimerRef.current) clearTimeout(universeCloseTimerRef.current);
    universeCloseTimerRef.current = setTimeout(() => {
      setUniverseOpen(false);
      universeCloseTimerRef.current = null;
    }, MEGA_CLOSE_DELAY_MS);
  }, []);

  const keepUniverseOpen = useCallback(() => {
    if (universeCloseTimerRef.current) {
      clearTimeout(universeCloseTimerRef.current);
      universeCloseTimerRef.current = null;
    }
    setUniverseOpen(true);
  }, []);

  const scheduleCloseNavVariant = useCallback(() => {
    if (navCloseTimerRef.current) clearTimeout(navCloseTimerRef.current);
    navCloseTimerRef.current = setTimeout(() => {
      setActiveNavVariant(null);
      navCloseTimerRef.current = null;
    }, MEGA_CLOSE_DELAY_MS);
  }, []);

  const keepNavVariantOpen = useCallback(() => {
    if (navCloseTimerRef.current) {
      clearTimeout(navCloseTimerRef.current);
      navCloseTimerRef.current = null;
    }
  }, []);

  // Sync position on scroll/resize while either mega panel is open
  useEffect(() => {
    if (!universeOpen && !activeNavVariant) return;
    updateUniverseTop();
    window.addEventListener('scroll', updateUniverseTop, true);
    window.addEventListener('resize', updateUniverseTop);
    return () => {
      window.removeEventListener('scroll', updateUniverseTop, true);
      window.removeEventListener('resize', updateUniverseTop);
    };
  }, [universeOpen, activeNavVariant, updateUniverseTop]);

  useEffect(() => {
    if (!storesMegaOpen) return;
    updateStoresMegaTop();
    window.addEventListener('scroll', updateStoresMegaTop, true);
    window.addEventListener('resize', updateStoresMegaTop);
    return () => {
      window.removeEventListener('scroll', updateStoresMegaTop, true);
      window.removeEventListener('resize', updateStoresMegaTop);
    };
  }, [storesMegaOpen, updateStoresMegaTop]);

  // Escape key closes
  useEffect(() => {
    if (!universeOpen && !storesMegaOpen) return;
    const onEsc = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (universeCloseTimerRef.current) {
        clearTimeout(universeCloseTimerRef.current);
        universeCloseTimerRef.current = null;
      }
      if (storesMegaCloseTimerRef.current) {
        clearTimeout(storesMegaCloseTimerRef.current);
        storesMegaCloseTimerRef.current = null;
      }
      setUniverseOpen(false);
      setStoresMegaOpen(false);
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [storesMegaOpen, universeOpen]);

  // Cleanup timers on unmount
  useEffect(
    () => () => {
      if (storesMegaCloseTimerRef.current) clearTimeout(storesMegaCloseTimerRef.current);
      if (universeCloseTimerRef.current) clearTimeout(universeCloseTimerRef.current);
      if (navCloseTimerRef.current) clearTimeout(navCloseTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    setSearchOpen(false);
    setActiveNavVariant(null);
    setUniverseOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!mobileOpen) {
      setMobileExpandedSection(null);
    }
  }, [mobileOpen]);

  const toggleMobileSection = useCallback((sectionId: string) => {
    setMobileExpandedSection((current) => (current === sectionId ? null : sectionId));
  }, []);

  const NAV: NavItem[] = [
    { to: '/', label: t('header.nav.home') },
    { to: '/stores', label: t('header.nav.stores'), variant: 'collections' },
    { to: '/music', label: t('header.nav.music') },
    { to: '/subscription', label: t('header.nav.subscriptions') },
    { to: '/news', label: t('header.nav.news') },
    { to: 'https://skoleom.com/contact', label: t('header.nav.support') },
  ];

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > SCROLL_OPAQUE_THRESHOLD);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // ⌘K / Ctrl+K opens search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const currentLanguage = I18N_TRANSLATION_ENABLED ? i18n.language : I18N_DEFAULT_LANGUAGE;
  const localizedNavLanguage = isSupportedLanguage(currentLanguage)
    ? currentLanguage
    : I18N_DEFAULT_LANGUAGE;

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={cn(
          'fixed left-0 right-0 top-0 z-50 overflow-visible text-white transition-[background] duration-500 ease-out',
          storesMegaOpen
            ? 'bg-black'
            : scrolled
              ? 'bg-gradient-to-b from-white/12 via-white/8 to-transparent backdrop-blur-[2px]'
              : 'bg-transparent',
        )}
      >
        <div ref={headerShellRef}>
          <div
            className={cn(
              'border-b border-white/[0.08] bg-black px-3 py-0 shadow-[0_10px_35px_rgba(0,0,0,0.35)] transition-[border-color,box-shadow] duration-500 ease-out sm:px-4 md:px-[60px]',
            )}
          >
            <div className="mx-auto flex h-14 max-w-[1600px] items-center justify-between gap-2 sm:gap-3 md:h-[71px]">
              <div className="flex shrink-0 items-center gap-4 sm:gap-5 md:gap-6">
                <BrandLogo />
                <UniverseButton
                  open={universeOpen}
                  onMouseEnter={openUniverse}
                  onMouseLeave={scheduleCloseUniverse}
                  onClick={() => (universeOpen ? scheduleCloseUniverse() : openUniverse())}
                />
              </div>

              {!hideSearchTrigger ? (
                <div className="hidden min-w-0 flex-1 items-center justify-center px-2 md:flex lg:px-4">
                  <button
                    type="button"
                    onClick={() => setSearchOpen(true)}
                    className="flex h-9 w-full max-w-[420px] items-center gap-2 rounded-[50px] border border-white/[0.06] bg-[#282828] px-4 text-xs text-[#B5B5B5] shadow-inner shadow-black/40 transition duration-300 hover:bg-[#2f2f2f] hover:text-white"
                    aria-label={t('common.search')}
                  >
                    <Search size={13} className="shrink-0" />
                    <span className="flex-1 text-left">{t('Search')}</span>
                    <kbd className="rounded  border-white/10 px-1.5 py-0.5 text-[9px] text-white/30">
                      ⌘K
                    </kbd>
                  </button>
                </div>
              ) : (
                <div className="hidden min-w-0 flex-1 md:flex" />
              )}

              <HeaderActions
                headerShellRef={headerShellRef}
                showWeather={false}
                hideSearchTrigger={hideSearchTrigger}
                onSearchClick={() => setSearchOpen(true)}
                onMobileMenuOpen={() => setMobileOpen(true)}
              />
            </div>
          </div>

          <div
            role="navigation"
            aria-label={t('header.navLabel')}
            className="mx-auto mt-1 hidden h-auto w-fit max-w-[min(calc(100vw-3rem),1600px)] flex-nowrap items-center justify-center gap-1 overflow-visible rounded-full border border-white/[0.18] bg-black px-2 py-1.5 font-sans text-white shadow-[0_0_0_1px_rgba(255,255,255,0.10),inset_0_1px_0_0_rgba(255,255,255,0.07),0_12px_34px_-8px_rgba(0,0,0,0.82),0_0_44px_rgba(0,0,0,0.42)] ring-1 ring-inset ring-black/45 lg:flex"
          >
            {NAV.map((item) => {
              const isExternal = /^https?:\/\//.test(item.to);
              const link = isExternal ? (
                <a
                  key={item.to}
                  href={item.to}
                  className={cn(
                    'inline-flex min-h-[30px] items-center justify-center whitespace-nowrap rounded-full px-3 py-1 text-center font-sans text-xs font-semibold tracking-wide drop-shadow-[0_1px_1px_rgba(0,0,0,0.85)] transition duration-300 xl:px-3.5 text-white/80 hover:bg-white/[0.10] hover:text-skoleom-lime',
                  )}
                >
                  {item.label}
                </a>
              ) : (
                <NavLink
                  key={item.to}
                  to={buildLocalizedPath(item.to, localizedNavLanguage)}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    cn(
                      'inline-flex min-h-[30px] items-center justify-center whitespace-nowrap rounded-full px-3 py-1 text-center font-sans text-xs font-semibold tracking-wide drop-shadow-[0_1px_1px_rgba(0,0,0,0.85)] transition duration-300 xl:px-3.5',
                      isActive
                        ? 'bg-white/[0.10] text-skoleom-lime'
                        : 'text-white/80 hover:bg-white/[0.10] hover:text-skoleom-lime',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              );

              if (item.to !== '/stores') return link;

              return (
                <div
                  key={item.to}
                  ref={storesTriggerRef}
                  onMouseEnter={openStoresMega}
                  onMouseLeave={scheduleCloseStoresMega}
                >
                  {link}
                </div>
              );
            })}
          </div>

          <Navbar
            open={activeNavVariant !== null}
            onMouseEnter={keepNavVariantOpen}
            onMouseLeave={scheduleCloseNavVariant}
            variant={activeNavVariant ?? 'collections'}
            topPx={universeTopPx || 104}
          />
        </div>
      </motion.header>

      {typeof document !== 'undefined' &&
        createPortal(
          <Navbar
            open={storesMegaOpen}
            variant="collections"
            topPx={storesMegaTopPx || 104}
            onMouseEnter={keepStoresMegaOpen}
            onMouseLeave={scheduleCloseStoresMega}
          />,
          document.body,
        )}

      {typeof document !== 'undefined' &&
        createPortal(
          <UniverseMegaMenu
            open={universeOpen}
            topPx={universeTopPx || 104}
            onClose={() => setUniverseOpen(false)}
            onMouseEnter={keepUniverseOpen}
            onMouseLeave={scheduleCloseUniverse}
          />,
          document.body,
        )}

      {/* ── Mobile menu ─────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30 }}
            className="fixed inset-0 z-[60] overflow-y-auto overscroll-contain bg-black/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex items-center justify-between border-b border-white/10 p-6">
              <BrandLogo />
              <button
                onClick={() => setMobileOpen(false)}
                className="rounded-full p-2 hover:bg-white/10"
                aria-label="Close menu"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="space-y-1 px-3 pb-4 pt-4">
              <MobileAccordionItem
                id="universe"
                label={t('header.universe')}
                expanded={mobileExpandedSection === 'universe'}
                onToggle={() => toggleMobileSection('universe')}
                highlight
              >
                <MobileUniversePanel onNavigate={() => setMobileOpen(false)} />
              </MobileAccordionItem>

              {NAV.map((it) => {
                const isExternal = /^https?:\/\//.test(it.to);

                return isExternal ? (
                  <a
                    key={it.to}
                    href={it.to}
                    onClick={() => setMobileOpen(false)}
                    className="block border-b border-white/5 py-4 font-sans text-xl font-semibold tracking-tight text-white md:text-2xl"
                  >
                    {it.label}
                  </a>
                ) : (
                  <Link
                    key={it.to}
                    to={buildLocalizedPath(it.to, localizedNavLanguage)}
                    onClick={() => setMobileOpen(false)}
                    className="block border-b border-white/5 py-4 font-sans text-xl font-semibold tracking-tight text-white md:text-2xl"
                  >
                    {it.label}
                  </Link>
                );
              })}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {searchOpen && (
          <Suspense fallback={null}>
            <SearchModal onClose={() => setSearchOpen(false)} />
          </Suspense>
        )}
      </AnimatePresence>
    </>
  );
}

function BrandLogo() {
  const { t, i18n } = useTranslation();
  const localizedLanguage = isSupportedLanguage(i18n.language)
    ? i18n.language
    : I18N_DEFAULT_LANGUAGE;

  return (
    <Link
      to={buildLocalizedPath('/', localizedLanguage)}
      className="group inline-flex shrink-0 items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-skoleom-lime focus-visible:rounded"
      aria-label={t('header.brandHome')}
    >
      <Image
        src="/assets/images/logos/logo-name-white-2-vectorized-1.svg"
        alt="SkoleToon logo"
        width={4096}
        height={986}
        sizes="(max-width: 640px) 96px, 160px"
        className="h-4 w-auto object-contain transition duration-300 group-hover:opacity-90 sm:h-6  "
      />
    </Link>
  );
}

interface UniverseButtonProps {
  open: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
}

function UniverseButton({ open, onMouseEnter, onMouseLeave, onClick }: UniverseButtonProps) {
  const { t } = useTranslation();

  return (
    <div
      className="relative hidden md:block"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        type="button"
        onClick={onClick}
        aria-label={t('header.universe')}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls="skoleom-universe-mega-panel"
        className={cn(
          'inline-flex cursor-pointer select-none items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-medium text-white/90 transition duration-300 hover:text-skoleom-lime sm:text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-skoleom-lime focus-visible:ring-offset-2 focus-visible:ring-offset-black',
          open && 'text-skoleom-lime',
        )}
      >
        <span>{t('header.universe')}</span>
        <AlignJustify
          size={13}
          className={cn(
            'transition-transform duration-300',
            open ? 'rotate-90 text-skoleom-lime' : 'text-white/80',
          )}
          aria-hidden
        />
      </button>
    </div>
  );
}

interface IconButtonProps {
  label: string;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}

function IconButton({ label, children, className, onClick }: IconButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white/85 transition duration-300 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-skoleom-lime focus-visible:ring-offset-2 focus-visible:ring-offset-black',
        className,
      )}
      aria-label={label}
    >
      {children}
    </button>
  );
}

interface HeaderActionsProps {
  headerShellRef: RefObject<HTMLDivElement | null>;
  showWeather: boolean;
  hideSearchTrigger: boolean;
  onSearchClick: () => void;
  onMobileMenuOpen: () => void;
}

function HeaderActions({
  headerShellRef,
  hideSearchTrigger,
  onSearchClick,
  onMobileMenuOpen,
}: HeaderActionsProps) {
  const { t } = useTranslation();

  return (
    <div className="flex shrink-0 items-center justify-end gap-1.5 text-white/85 sm:gap-2">
      {!hideSearchTrigger ? (
        <IconButton label={t('common.search')} onClick={onSearchClick} className="lg:hidden">
          <Search size={15} />
        </IconButton>
      ) : null}

      <LanguageButton headerShellRef={headerShellRef} />
      <AccessibilityTrigger />

      <IconButton
        label={t('header.actions.openMenu')}
        onClick={onMobileMenuOpen}
        className="lg:hidden"
      >
        <Menu size={17} />
      </IconButton>
    </div>
  );
}

function LanguageButton({ headerShellRef }: { headerShellRef: RefObject<HTMLDivElement | null> }) {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const updateMenuPosition = useCallback(() => {
    const header = headerShellRef.current;
    const trigger = rootRef.current;
    if (!header || !trigger) return;

    const headerRect = header.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const gap = 12;
    const width = Math.min(320, window.innerWidth - 16);
    const isNarrow = window.innerWidth < 768;

    setMenuPos({
      top: headerRect.bottom + gap,
      left: isNarrow ? 8 : Math.max(8, triggerRect.right - width),
      width: isNarrow ? window.innerWidth - 16 : width,
    });
  }, [headerShellRef]);

  const frenchOption =
    languageOptions.find((option) => option.code === I18N_DEFAULT_LANGUAGE) || languageOptions[1];

  const currentOption =
    languageOptions.find((option) => option.code === i18n.language) || frenchOption;

  const zoneLabel = (zoneId: LanguageZoneId) => t(`header.language.zones.${zoneId}`);

  const handleLanguageChange = (languageCode: string) => {
    if (!I18N_TRANSLATION_ENABLED) {
      setOpen(false);
      return;
    }
    if (!isSupportedLanguage(languageCode)) {
      setOpen(false);
      return;
    }
    i18n.changeLanguage(languageCode);
    if (typeof window !== 'undefined') {
      setStorageItem('skoleom-language', languageCode);
      document.documentElement.lang = languageCode;
      document.documentElement.dir = isRtlLanguage(languageCode) ? 'rtl' : 'ltr';
      const localizedPath = buildLocalizedPath(
        stripLanguagePrefix(location.pathname),
        languageCode,
      );
      const target = `${localizedPath}${location.search}${location.hash}`;
      window.location.replace(target);
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, { passive: true });
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const handleEsc = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-9 shrink-0 items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2 text-[10px] font-semibold uppercase leading-none tracking-wide text-white/90 transition duration-300 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-skoleom-lime focus-visible:ring-offset-2 focus-visible:ring-offset-black sm:gap-1.5 sm:px-2.5"
        aria-label={t('header.language.change')}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <img
          src={`/icons/flags/${currentOption.flagCode}.svg`}
          alt=""
          aria-hidden
          className="h-3.5 w-5 rounded-[2px] object-cover"
        />
        <span className="hidden md:inline">{currentOption.code.toUpperCase()}</span>
        <ChevronDown
          size={10}
          className={cn('transition duration-300', open && 'rotate-180')}
          aria-hidden
        />
      </button>

      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {open && menuPos && (
              <motion.div
                ref={menuRef}
                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                transition={{ duration: 0.22, ease: 'easeOut' }}
                style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}
                className="fixed z-[70] max-h-[min(60dvh,30rem)] overflow-y-auto rounded-3xl border border-white/10 bg-[#111111]/95 p-2 shadow-2xl backdrop-blur-xl custom-scrollbar"
                role="listbox"
                aria-label={t('header.language.change')}
              >
                {getLanguageZoneGroups().map((group: LanguageZoneGroup, groupIndex: number) => (
                  <div
                    key={group.id}
                    className={cn(groupIndex > 0 && 'mt-2 pt-2')}
                    role="group"
                    aria-label={zoneLabel(group.id)}
                  >
                    <p className="px-3 pb-1 pt-1 text-[10px] font-bold uppercase tracking-[0.22em] text-white/40">
                      {zoneLabel(group.id as LanguageZoneId)}
                    </p>
                    {group.languages.map((option: LanguageOption) => {
                      const active = option.code === currentOption.code;
                      const disabled =
                        !I18N_TRANSLATION_ENABLED && option.code !== I18N_DEFAULT_LANGUAGE;

                      return (
                        <button
                          key={option.code}
                          type="button"
                          disabled={disabled}
                          onClick={() => handleLanguageChange(option.code)}
                          className={cn(
                            'flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-skoleom-lime',
                            disabled ? 'cursor-default opacity-40' : 'hover:bg-white/10',
                            active && 'bg-white/[0.08] text-white',
                          )}
                          role="option"
                          aria-selected={active}
                          aria-disabled={disabled || undefined}
                          lang={option.code}
                        >
                          <img
                            src={`/icons/flags/${option.flagCode}.svg`}
                            alt=""
                            aria-hidden
                            className="h-4 w-6 shrink-0 rounded-[2px] object-cover"
                          />
                          <span className="min-w-0 flex-1 text-lg font-medium text-[#D0D0D0]">
                            {option.nativeName}
                          </span>
                          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/45">
                            {option.code}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </div>
  );
}
